import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import {
  QueryMode,
  StorePerformanceItem,
  QuestionnairePerformance,
  PerformanceTab,
  ProcessStatusQueryParams,
  PieChartStoreData,
  PieChartPaginationInfo,
  ResultsData,
} from '../types'
import { ScheduleService } from '../../../../../../../service/schedule'
import { StoreScheduledPerformanceParams, StoreScheduledPerformancePieChartParams } from '../../../../../../../service/schedule/response'
import { httpFetch } from '../../../../../../../utils/crud'
import { buildQueryParams, buildQuestionnaireIds, getConsultedQuestionnaireItems, getCompanyId } from '../utils/buildQueryParams'
import { ITEMS_PER_PAGE, PIE_CHART_PAGE_SIZE, SESSION_STORAGE_RESULTS_KEY } from '../constants'

interface UseResultsConsultProps {
  queryMode: QueryMode
  selectedUnits: StorePerformanceItem[]
  selectedQuestionnaires: StorePerformanceItem[]
  canConsult: boolean
  hasAssignedCompany: boolean
  assignedCompany: { id: number | string } | null | undefined
  selectedCompany: StorePerformanceItem | null
  appliedPeriodFrom: Date | null
  appliedPeriodTo: Date | null
  appliedPeriodYear: number
  appliedPeriodWeek: string[]
  filterDivision: StorePerformanceItem[]
  filterZone: StorePerformanceItem[]
  filterUser: StorePerformanceItem[]
  filterRole: StorePerformanceItem[]
  selectedVersionsByQuestionnaire: Record<string, string[]>
  showSnackbar: (message: string, type?: 'success' | 'error' | 'warning' | 'info', interpolationOptions?: Record<string, unknown>) => void
}

interface UseResultsConsultReturn {
  hasQueried: boolean
  setHasQueried: (value: boolean) => void
  resultsData: ResultsData
  resultsLoading: boolean
  lastConsultTime: Date | null
  lastConsultTimeFormatted: string | null
  activeTab: PerformanceTab
  handleTabChange: (tab: PerformanceTab) => void
  selectedResultIds: string[]
  setSelectedResultIds: (ids: string[]) => void
  toggleResultItem: (id: string) => void
  selectAllResultItems: () => void
  searchResult: string
  setSearchResult: (value: string) => void
  filteredResultItems: QuestionnairePerformance[]
  resultsPage: number
  setResultsPage: (page: number) => void
  totalPages: number
  paginatedResultItems: QuestionnairePerformance[]
  performanceData: QuestionnairePerformance[]
  handleConsult: () => Promise<void>
  consultedItems: StorePerformanceItem[]
  setConsultedItems: (items: StorePerformanceItem[]) => void
  appliedProcessStatusParams: ProcessStatusQueryParams | null
  processStatusQueryParams: ProcessStatusQueryParams
  pieChartData: PieChartStoreData[]
  pieChartLoading: boolean
  pieChartPagination: PieChartPaginationInfo
  filteredPieChartData: PieChartStoreData[]
  handlePieChartPageChange: (page: number) => void
  chartType: 'bar' | 'line' | 'pie'
  setChartType: (type: 'bar' | 'line' | 'pie') => void
  handleChartTypeChange: (type: 'bar' | 'line' | 'pie') => void
  globalAverageScore: number | null
  handleRangeApplied: (range: [number, number]) => void
  appliedCriticalRange: [number, number]
  clearResults: () => void
}

const useResultsConsult = ({
  queryMode,
  selectedUnits,
  selectedQuestionnaires,
  canConsult,
  hasAssignedCompany,
  assignedCompany,
  selectedCompany,
  appliedPeriodFrom,
  appliedPeriodTo,
  appliedPeriodYear,
  appliedPeriodWeek,
  filterDivision,
  filterZone,
  filterUser,
  filterRole,
  selectedVersionsByQuestionnaire,
  showSnackbar,
}: UseResultsConsultProps): UseResultsConsultReturn => {
  const [hasQueried, setHasQueried] = useState(false)
  const [resultsData, setResultsData] = useState<ResultsData>({})
  const [resultsLoading, setResultsLoading] = useState(false)
  const [lastConsultTime, setLastConsultTime] = useState<Date | null>(null)
  const [activeTab, setActiveTab] = useState<PerformanceTab>('performance')
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie'>('bar')
  const [selectedResultIds, setSelectedResultIds] = useState<string[]>([])
  const [searchResult, setSearchResultState] = useState('')
  const [resultsPage, setResultsPageState] = useState(1)
  const [consultedItems, setConsultedItems] = useState<StorePerformanceItem[]>([])
  const [appliedProcessStatusParams, setAppliedProcessStatusParams] = useState<ProcessStatusQueryParams | null>(null)
  const [performanceData, setPerformanceData] = useState<QuestionnairePerformance[]>([])
  const [pieChartData, setPieChartData] = useState<PieChartStoreData[]>([])
  const [pieChartLoading, setPieChartLoading] = useState(false)
  const [pieChartPagination, setPieChartPagination] = useState<PieChartPaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: PIE_CHART_PAGE_SIZE,
  })
  const [appliedCriticalRange, setAppliedCriticalRange] = useState<[number, number]>([0, 60])

  useEffect(() => {
    const raw = sessionStorage.getItem(SESSION_STORAGE_RESULTS_KEY)
    if (raw) {
      try {
        const saved = JSON.parse(raw)
        if (saved.hasQueried) setHasQueried(saved.hasQueried)
        if (saved.resultsData) setResultsData(saved.resultsData)
        if (saved.performanceData) setPerformanceData(saved.performanceData)
        if (saved.consultedItems) setConsultedItems(saved.consultedItems)
        if (saved.selectedResultIds) setSelectedResultIds(saved.selectedResultIds)
        if (saved.activeTab) setActiveTab(saved.activeTab)
        if (saved.chartType) setChartType(saved.chartType)
        if (saved.appliedProcessStatusParams) setAppliedProcessStatusParams(saved.appliedProcessStatusParams)
        if (saved.appliedCriticalRange) setAppliedCriticalRange(saved.appliedCriticalRange)
        if (saved.lastConsultTime) setLastConsultTime(new Date(saved.lastConsultTime))
        if (saved.pieChartData) setPieChartData(saved.pieChartData)
        if (saved.pieChartPagination) setPieChartPagination(saved.pieChartPagination)
        if (saved.resultsPage) setResultsPageState(saved.resultsPage)
        sessionStorage.removeItem(SESSION_STORAGE_RESULTS_KEY)
      } catch {
        sessionStorage.removeItem(SESSION_STORAGE_RESULTS_KEY)
      }
    }
  }, [])

  const companyId = getCompanyId(hasAssignedCompany, assignedCompany, selectedCompany)

  const handleConsultPieChart = useCallback(async (page = 1, resultIds?: string[], skipAutoSelect?: boolean) => {
    if (!canConsult) return

    setPieChartLoading(true)
    try {
      const idsToUse = resultIds && resultIds.length > 0 ? resultIds : undefined
      const storeIds = queryMode === 'many-units-one-questionnaire' && idsToUse
        ? idsToUse.join(',')
        : selectedUnits.map((u) => u.id).join(',')
      const questionnaireIds = (queryMode === 'one-unit-many-questionnaires' || queryMode === 'one-unit-one-questionnaire') && idsToUse
        ? idsToUse.join(',')
        : buildQuestionnaireIds(selectedQuestionnaires, selectedVersionsByQuestionnaire).join(',')

      const params: StoreScheduledPerformancePieChartParams = {
        company_id: companyId,
        store_ids: storeIds,
        questionnaire_ids: questionnaireIds,
        date_init: appliedPeriodFrom ? appliedPeriodFrom.toISOString().split('T')[0] : undefined,
        date_end: appliedPeriodTo ? appliedPeriodTo.toISOString().split('T')[0] : undefined,
        year: appliedPeriodYear,
        weeks: appliedPeriodWeek.length > 0 ? appliedPeriodWeek.join(',') : undefined,
        division_ids: filterDivision.length > 0 ? filterDivision.map((d) => d.value).join(',') : undefined,
        zone_ids: filterZone.length > 0 ? filterZone.map((z) => z.value).join(',') : undefined,
        user_ids: filterUser.length > 0 ? filterUser.map((u) => u.value).join(',') : undefined,
        role_ids: filterRole.length > 0 ? filterRole.map((r) => r.value).join(',') : undefined,
        page,
        size: PIE_CHART_PAGE_SIZE,
      }

      const response = await ScheduleService(httpFetch).getStoreScheduledPerformancePieChart(params)

      const scoreMap = new Map<number, number>()
      response.scoreTotal.forEach((st) => scoreMap.set(st.id, st.value))

      const enrichedItems: PieChartStoreData[] = response.data.items.map((item) => {
        const scoreId = response.viewMode === 'byStore' ? item.storeId : item.questionnaireId
        return {
          ...item,
          score: scoreMap.get(scoreId) ?? 0,
        }
      })

      setPieChartData(enrichedItems)
      setPieChartPagination({
        currentPage: response.data.currentPage,
        totalPages: response.data.totalPages,
        totalItems: response.data.totalItems,
        limit: response.data.limit,
      })

      if (!skipAutoSelect) {
        const newIds = enrichedItems.map(item =>
          queryMode === 'many-units-one-questionnaire'
            ? String(item.storeId)
            : String(item.questionnaireId)
        )
        setSelectedResultIds(newIds)
      }
    } catch {
      showSnackbar('dashboard.storePerformance.error.loadPieChart')
      setPieChartData([])
      setPieChartPagination({ currentPage: 1, totalPages: 1, totalItems: 0, limit: PIE_CHART_PAGE_SIZE })
    } finally {
      setPieChartLoading(false)
    }
  }, [canConsult, queryMode, selectedUnits, selectedQuestionnaires, selectedVersionsByQuestionnaire, companyId, appliedPeriodFrom, appliedPeriodTo, appliedPeriodYear, appliedPeriodWeek, filterDivision, filterZone, filterUser, filterRole, showSnackbar])

  const handleConsult = useCallback(async () => {
    if (!canConsult) return

    const snapshotItems = queryMode === 'many-units-one-questionnaire'
      ? selectedUnits
      : getConsultedQuestionnaireItems(selectedQuestionnaires, selectedVersionsByQuestionnaire)
    setConsultedItems(snapshotItems)

    const params = buildQueryParams({
      companyId,
      selectedUnits,
      selectedQuestionnaires,
      appliedPeriodFrom,
      appliedPeriodTo,
      appliedPeriodYear,
      appliedPeriodWeek,
      filterDivision,
      filterZone,
      filterUser,
      filterRole,
      selectedVersionsByQuestionnaire,
    })

    setAppliedProcessStatusParams({
      companyId,
      storeIds: params.store_ids,
      questionnaireIds: params.questionnaire_ids,
      dateInit: params.date_init,
      dateEnd: params.date_end,
      year: params.year,
      weeks: params.weeks,
      divisionIds: params.division_ids,
      zoneIds: params.zone_ids,
      userIds: params.user_ids,
      roleIds: params.role_ids,
    })

    setHasQueried(true)
    setResultsLoading(true)
    setResultsPageState(1)
    setSearchResultState('')
    setLastConsultTime(new Date())

    try {
      const response = await ScheduleService(httpFetch).getStoreScheduledPerformance(params as StoreScheduledPerformanceParams)

      const selectedIds = snapshotItems.map((item) => Number(item.id))
      const idsWithData = response.scoreTotal.map((st) => st.id)
      const idsWithoutData = selectedIds.filter((id) => !idsWithData.includes(id))

      if (idsWithoutData.length > 0) {
        const itemsWithoutData = snapshotItems.filter((item) => idsWithoutData.includes(Number(item.id)))
        
        let alertMessage = ''
        if (queryMode === 'one-unit-many-questionnaires' || queryMode === 'one-unit-one-questionnaire') {
          alertMessage = itemsWithoutData.length === 1
            ? 'dashboard.storePerformance.noDataAlert.oneQuestionnaire'
            : 'dashboard.storePerformance.noDataAlert.multipleQuestionnaires'
        } else {
          alertMessage = itemsWithoutData.length === 1
            ? 'dashboard.storePerformance.noDataAlert.oneUnit'
            : 'dashboard.storePerformance.noDataAlert.multipleUnits'
        }

        if (itemsWithoutData.length === 1) {
          showSnackbar(alertMessage, 'info', { name: itemsWithoutData[0].label })
        } else {
          showSnackbar(alertMessage, 'info', { count: itemsWithoutData.length })
        }
      }

      const mapped = response.scoreTotal.map((scoreEntry) => ({
        id: String(scoreEntry.id),
        name: scoreEntry.label,
        score: scoreEntry.value,
        firstScheduledDate: scoreEntry.firstScheduledDate ?? null,
        lastScheduledDate: scoreEntry.lastScheduledDate ?? null,
      }))

      const newResultIds = mapped.map((item) => item.id)
      setPerformanceData(mapped)
      setSelectedResultIds(newResultIds)
      setResultsData(response.data ?? {})

      handleConsultPieChart(1, newResultIds, true).catch(() => {})
    } catch {
      showSnackbar('dashboard.storePerformance.error.loadResults')
      setPerformanceData([])
    } finally {
      setResultsLoading(false)
    }
  }, [canConsult, queryMode, selectedUnits, selectedQuestionnaires, selectedVersionsByQuestionnaire, companyId, appliedPeriodFrom, appliedPeriodTo, appliedPeriodYear, appliedPeriodWeek, filterDivision, filterZone, filterUser, filterRole, showSnackbar, handleConsultPieChart])

  const filteredResultItems = useMemo(() => {
    if (!searchResult.trim()) return performanceData
    const term = searchResult.toLowerCase()
    return performanceData.filter((item) => item.name.toLowerCase().includes(term))
  }, [performanceData, searchResult])

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredResultItems.length / ITEMS_PER_PAGE))
  }, [filteredResultItems])

  const paginatedResultItems = useMemo(() => {
    const start = (resultsPage - 1) * ITEMS_PER_PAGE
    return filteredResultItems.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredResultItems, resultsPage])

  const toggleResultItem = useCallback((id: string) => {
    setSelectedResultIds((prev) => {
      const scopeItems = chartType === 'pie' ? paginatedResultItems : performanceData
      const allSelected = prev.length === scopeItems.length && scopeItems.length > 1
      if (allSelected) {
        return [id]
      }
      if (prev.includes(id)) {
        return prev.filter((itemId) => itemId !== id)
      }
      return [...prev, id]
    })
  }, [chartType, paginatedResultItems, performanceData])

  const selectAllResultItems = useCallback(() => {
    setSelectedResultIds((prev) => {
      const scopeItems = chartType === 'pie' ? paginatedResultItems : performanceData
      if (prev.length === scopeItems.length) {
        return []
      }
      return scopeItems.map((item) => item.id)
    })
  }, [chartType, paginatedResultItems, performanceData])

  const handleTabChange = useCallback((tab: PerformanceTab) => {
    setActiveTab(tab)
  }, [])

  const handleChartTypeChange = useCallback((type: 'bar' | 'line' | 'pie') => {
    setChartType(type)
  }, [])

  const handlePieChartPageChange = useCallback((page: number) => {
    setResultsPageState(page)
    setPieChartPagination(prev => ({ ...prev, currentPage: page }))
    handleConsultPieChart(page)
  }, [handleConsultPieChart])

  const setSearchResult = useCallback((value: string) => {
    setSearchResultState(value)
    setResultsPageState(1)
  }, [])

  const setResultsPage = useCallback((page: number) => {
    setResultsPageState(page)
    if (chartType === 'pie') {
      const start = (page - 1) * ITEMS_PER_PAGE
      const newPageIds = filteredResultItems.slice(start, start + ITEMS_PER_PAGE).map(item => item.id)
      setSelectedResultIds(newPageIds)
      if (hasQueried) {
        setPieChartPagination(prev => ({ ...prev, currentPage: page }))
        handleConsultPieChart(page, undefined, true)
      }
    }
  }, [chartType, hasQueried, handleConsultPieChart, filteredResultItems])

  const lastConsultTimeFormatted = useMemo(() => {
    if (!lastConsultTime) return null
    const hours = lastConsultTime.getHours()
    const minutes = lastConsultTime.getMinutes()
    const ampm = hours >= 12 ? 'p.m.' : 'a.m.'
    const hours12 = hours % 12 || 12
    const minutesStr = minutes.toString().padStart(2, '0')
    return `${hours12}:${minutesStr} ${ampm}`
  }, [lastConsultTime])

  const filteredPieChartData = useMemo(() => {
    if (selectedResultIds.length === 0 || pieChartData.length === 0) return []
    return pieChartData.filter((item) => {
      const idToMatch = queryMode === 'many-units-one-questionnaire'
        ? String(item.storeId)
        : String(item.questionnaireId)
      return selectedResultIds.includes(idToMatch)
    })
  }, [pieChartData, selectedResultIds, queryMode])

  const globalAverageScore = useMemo(() => {
    if (!resultsData || typeof resultsData !== 'object' || Array.isArray(resultsData)) {
      return null
    }
    if (consultedItems.length === 0) return null
    const rawData = resultsData as ResultsData
    const allWeeks = Object.keys(rawData)
    if (allWeeks.length === 0) return null

    const isStoreMode = queryMode === 'many-units-one-questionnaire'
    const consultedIds = new Set(consultedItems.map(item => item.id))

    let totalSum = 0
    let totalCount = 0
    for (const week of allWeeks) {
      for (const record of rawData[week] ?? []) {
        const id = isStoreMode ? String(record.storeId) : String(record.questionnaireId)
        if (consultedIds.has(id) && record.score != null) {
          totalSum += record.score
          totalCount++
        }
      }
    }
    return totalCount > 0 ? Math.round(totalSum / totalCount) : null
  }, [resultsData, consultedItems, queryMode])

  const processStatusQueryParams = useMemo(() => {
    if (appliedProcessStatusParams) {
      return appliedProcessStatusParams
    }
    return {
      companyId,
      storeIds: '',
      questionnaireIds: '',
      year: appliedPeriodYear,
    }
  }, [appliedProcessStatusParams, companyId, appliedPeriodYear])

  const handleRangeApplied = useCallback((range: [number, number]) => {
    setAppliedCriticalRange(range)
  }, [])

  const clearResults = useCallback(() => {
    setHasQueried(false)
    setResultsData({})
    setPerformanceData([])
    setConsultedItems([])
    setSelectedResultIds([])
    setAppliedProcessStatusParams(null)
    setLastConsultTime(null)
    setActiveTab('performance')
    setChartType('bar')
    setSearchResultState('')
    setResultsPageState(1)
    setPieChartData([])
    setPieChartPagination({ currentPage: 1, totalPages: 1, totalItems: 0, limit: PIE_CHART_PAGE_SIZE })
    setAppliedCriticalRange([0, 60])
  }, [])

  return {
    hasQueried,
    setHasQueried,
    resultsData,
    resultsLoading,
    lastConsultTimeFormatted,
    activeTab,
    handleTabChange,
    selectedResultIds,
    setSelectedResultIds,
    toggleResultItem,
    selectAllResultItems,
    searchResult,
    setSearchResult,
    filteredResultItems,
    resultsPage,
    setResultsPage,
    totalPages,
    paginatedResultItems,
    performanceData,
    handleConsult,
    consultedItems,
    setConsultedItems,
    appliedProcessStatusParams,
    processStatusQueryParams,
    pieChartData,
    pieChartLoading,
    pieChartPagination,
    filteredPieChartData,
    handlePieChartPageChange,
    chartType,
    setChartType,
    handleChartTypeChange,
    globalAverageScore,
    handleRangeApplied,
    appliedCriticalRange,
    lastConsultTime,
    clearResults,
  }
}

export { useResultsConsult }
